import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { UploadCloud, FileText, ArrowRight, Check } from "lucide-react";
import { apiRequest, WIZARD_STEPS as steps } from "../utils/api";
import VaultPicker from "../secure/VaultPicker";

const formatBytes = (n) => {
  if (n == null) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

export default function UploadArtifact() {
  const navigate = useNavigate();
  const currentStep = 1;
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [artifactInfo, setArtifactInfo] = useState([]);
  const [error, setError] = useState("");
  const [showMenu, setShowMenu] = useState(false);
  const [storeOnIpfs, setStoreOnIpfs] = useState(false);
  const [showVault, setShowVault] = useState(false);
  const fileInputRef = useRef(null);

  // Every file type is accepted here. The server decides what is allowed
  // (size limit, extension policy) and explains any rejection.
  const handleFile = async (file, fromVault = false) => {
    if (!file) return;
    setError("");
    setUploading(true);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("ipfs", storeOnIpfs ? "true" : "false");

    try {
      const data = await apiRequest("/api/artifact/upload", { method: "POST", formData });

      setUploadedFile({
        artifactId: data.artifact_id,
        name: data.filename,
        size: data.size,
        fileType: data.file_type,
        uploadedAt: data.uploaded_at,
        raw: file,
        fromVault,
        sha256: data.sha256,
        sha512: data.sha512,
      });

      setArtifactInfo([
        { label: "SHA-256 (computed by the server)", value: data.sha256, mono: true, wide: true },
        { label: "SHA-512 (computed by the server)", value: data.sha512, mono: true, wide: true },
        { label: "File Size", value: formatBytes(data.size) },
        { label: "File Type", value: data.file_type ? data.file_type.toUpperCase() : "No extension" },
        { label: "Detected Format", value: data.format || "Unknown" },
        ...(data.ipfs_cid ? [{ label: "IPFS CID", value: data.ipfs_cid, mono: true, wide: true }] : []),
      ]);
    } catch (err) {
      setUploadedFile(null);
      setArtifactInfo([]);
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const handleDragLeave = () => setDragging(false);
  const handleDrop = (e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); };
  const handleBrowseClick = () => fileInputRef.current?.click();
  const handleFileChange = (e) => handleFile(e.target.files[0]);
  const handleRemove = () => {
    // Removes the file from this screen only; the uploaded record stays on the server.
    setUploadedFile(null);
    setArtifactInfo([]);
    setError("");
    setShowMenu(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}>

      {/* STEPPER */}
      <div className="flex items-center justify-between relative mb-5" style={{ height: "72px" }}>
        {steps.map((step, index) => {
          const isDone = index < currentStep;
          const isCurrent = index === currentStep;

          let circleClass = "bg-gray-200 text-gray-600";
          let labelClass = "text-gray-500";
          if (isDone) {
            circleClass = "bg-green-500 text-white";
            labelClass = "text-green-600";
          } else if (isCurrent) {
            circleClass = "bg-blue-600 text-white";
            labelClass = "text-blue-600";
          }

          return (
          <div key={step} className="flex-1 flex flex-col items-center relative">
            {index !== steps.length - 1 && (
              <div className={`absolute top-5 left-1/2 w-full h-[2px] ${index < currentStep ? "bg-green-500" : "bg-gray-300"}`} />
            )}
            <div
              className={`z-10 rounded-full flex items-center justify-center text-xs font-semibold ${circleClass}`}
              style={{ width: "40px", height: "40px" }}
            >
              {index < currentStep ? <Check size={14} /> : index + 1}
            </div>
            <p className={`mt-2 text-[14px] font-medium text-center ${labelClass}`}>
              {step}
            </p>
          </div>
          );
        })}
      </div>

      {/* UPLOAD BOX */}
      <div
        className={`border-2 border-dashed rounded-2xl py-6 px-4 w-full max-w-2xl mx-auto text-center transition mb-4 ${
          dragging ? "border-blue-600 bg-blue-50" : "border-blue-300 hover:border-blue-600 hover:bg-blue-50"
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="flex justify-center mb-3">
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-blue-100 flex items-center justify-center shadow-md">
            <UploadCloud className="w-10 h-10 md:w-12 md:h-12 text-blue-600" />
          </div>
        </div>
        <h2 className="text-2xl font-semibold text-gray-700">Drag &amp; drop artifact / binary file here</h2>
        <p className="text-base font-normal text-gray-400 mt-2 mb-2">or</p>
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          onChange={handleFileChange}
        />
        <div className="flex flex-wrap items-center justify-center gap-3 mt-2">
          <button
            onClick={handleBrowseClick}
            disabled={uploading}
            className="inline-flex items-center justify-center h-[44px] px-6 bg-[#2563EB] text-white text-sm font-medium rounded-[10px] hover:bg-[#1D4ED8] transition disabled:opacity-60"
          >
            {uploading ? "Uploading…" : "Browse Files"}
          </button>
          <button
            onClick={() => setShowVault(true)}
            disabled={uploading}
            className="inline-flex items-center justify-center h-[44px] px-6 bg-white text-[#2563EB] border border-[#2563EB] text-sm font-medium rounded-[10px] hover:bg-blue-50 transition disabled:opacity-60"
          >
            Select from Secure Vault
          </button>
        </div>
        <p className="text-sm font-normal text-gray-400 mt-4">
          Any software artifact, archive, package, SBOM or metadata file can be signed.<br />
          The maximum size is set by your administrator.
        </p>

        <label className="inline-flex items-center gap-2 mt-3 text-sm text-gray-600 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={storeOnIpfs}
            onChange={(e) => setStoreOnIpfs(e.target.checked)}
          />
          Also store a copy on IPFS (optional — leave off for confidential files)
        </label>

        {error && <p className="text-sm text-red-500 mt-3">{error}</p>}
      </div>

      {/* UPLOADED FILE */}
      {uploadedFile && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold text-gray-700" style={{ fontSize: "17px" }}>Uploaded File</h3>
          </div>
          <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-2 rounded-lg">
                <FileText className="text-blue-600 w-4 h-4" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">
                  {uploadedFile.name}
                  {uploadedFile.fromVault && (
                    <span className="ml-2 px-2 py-0.5 text-[10px] rounded-full bg-green-100 text-green-700 font-medium">
                      Secure Vault
                    </span>
                  )}
                </p>
                <p className="text-xs text-gray-400">
                  {formatBytes(uploadedFile.size)} · artifact #{uploadedFile.artifactId}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-500 whitespace-nowrap">
                {uploadedFile.uploadedAt ? new Date(uploadedFile.uploadedAt).toLocaleString() : ""}
              </span>
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full whitespace-nowrap">
                Uploaded
              </span>
              <div className="relative">
                <button
                  onClick={() => setShowMenu((v) => !v)}
                  className="text-gray-400 hover:text-gray-600 font-bold px-1 text-lg leading-none tracking-widest"
                >···</button>
                {showMenu && (
                  <div className="absolute right-0 mt-1 w-36 bg-white border border-gray-200 rounded-lg shadow-lg z-10 overflow-hidden">
                    <button
                      onClick={() => { setShowMenu(false); handleRemove(); }}
                      className="w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-red-50"
                    >Upload a different file</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ARTIFACT DETAILS */}
      {artifactInfo.length > 0 && (
        <div className="mb-4">
          <h3 className="font-bold text-gray-700 mb-2" style={{ fontSize: "17px", lineHeight: "20px" }}>
            Artifact Details
          </h3>
          <div className="border border-gray-200 rounded-2xl p-4 shadow-sm bg-white">
            <div className="grid grid-cols-3 gap-4">
              {artifactInfo.map((item) => (
                <div key={item.label} style={item.wide ? { gridColumn: "1 / -1" } : undefined}>
                  <p className="text-xs text-gray-400">{item.label}</p>
                  <p
                    className="text-sm font-semibold text-gray-800 break-all"
                    style={item.mono ? { fontFamily: "monospace", fontSize: 12.5 } : undefined}
                  >
                    {item.value}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* SECURE VAULT PICKER: the unlocked file goes through the same upload as a browsed file */}
      <VaultPicker
        open={showVault}
        purpose="artifact_sign"
        onClose={() => setShowVault(false)}
        onSelect={(doc) => handleFile(doc.file, true)}
      />

      {/* FOOTER */}
      <div className="flex justify-between mt-2">
        <button
          onClick={() => navigate("/choose-method")}
          className="text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 transition rounded-[10px]"
          style={{ height: "44px", padding: "0 24px" }}
        >
          Back
        </button>
        <button
          disabled={uploading}
          onClick={() => {
            if (!uploadedFile) {
              setError("Please upload an artifact file first.");
              return;
            }
            navigate("/generate-manifest", {
              state: {
                artifactId: uploadedFile.artifactId,
                filename: uploadedFile.name,
                sha256: uploadedFile.sha256,
                sha512: uploadedFile.sha512,
              },
            });
          }}
          className="flex items-center gap-2 rounded-[10px] text-white bg-blue-600 hover:bg-blue-700 transition text-sm disabled:opacity-60"
          style={{ height: "44px", padding: "0 24px" }}
        >
          Next <ArrowRight size={14} />
        </button>
      </div>

    </div>
  );
}